/**
 * Format converters between user-friendly UI format and backend device format
 */

// Enum mappings
const METRIC_TO_SENSOR_TYPE = {
  Temperature: 0,
  Magnetometer: 1,
  Vibration: 2
};

const SENSOR_TYPE_TO_METRIC = {
  0: 'Temperature',
  1: 'Magnetometer',
  2: 'Vibration'
};

export const SPEED_METRIC_TYPE = 'Speed';

const METRIC_TYPE_TO_PARAMETER = {
  Temperature: 0,
  'Magnetic Flux': 1,
  'Accel RMS': 2,
  'Accel Peak': 3,
  'Accel P2P': 4,
  'Velocity RMS': 5,
  'Velocity Peak': 6,
  'Velocity P2P': 7,
  Speed: 8
};

const PARAMETER_TO_METRIC_TYPE = {
  0: 'Temperature',
  1: 'Magnetic Flux',
  2: 'Accel RMS',
  3: 'Accel Peak',
  4: 'Accel P2P',
  5: 'Velocity RMS',
  6: 'Velocity Peak',
  7: 'Velocity P2P',
  8: 'Speed'
};

const OPERATOR_TO_ENUM = {
  '>': 0,
  '<': 1,
  '≥': 2,
  '≤': 3,
  '=': 4,
  '≠': 5,
  OUTSIDE: 6,
  INSIDE: 7
};

const ENUM_TO_OPERATOR = {
  0: '>',
  1: '<',
  2: '≥',
  3: '≤',
  4: '=',
  5: '≠',
  6: 'OUTSIDE',
  7: 'INSIDE'
};

const LOGIC_TO_ENUM = {
  AND: 0,
  OR: 1
};

const ENUM_TO_LOGIC = {
  0: 'AND',
  1: 'OR'
};

// Mirrors the backend `DeviceEventActionTypeEnum`
// (cloud/app/models/helpers/device_event_enums.py).
const ACTION_TO_ENUM = {
  'Immediate Upload': 0,
  'Schedule Change': 1
};

const ENUM_TO_ACTION = {
  0: 'Immediate Upload',
  1: 'Schedule Change'
};

/**
 * Convert user-friendly threshold format to device format array
 */
export const convertThresholdsToDevice = (thresholds, metric) => {
  if (metric === 'Temperature' || metric === 'Magnetometer') {
    // Single value: { value: 85 } -> [85]
    return [thresholds.value || 0];
  } if (metric === 'Vibration') {
    // Triaxial: { x: 8.0, y: 8.0, z: 8.0 } -> [8.0, 8.0, 8.0]
    return [
      thresholds.x || 0,
      thresholds.y || 0,
      thresholds.z || 0
    ];
  }
  return [0];
};

/**
 * Convert device format array to user-friendly threshold format
 */
export const convertThresholdsToUser = (threshold, metric) => {
  if (!threshold || !Array.isArray(threshold)) {
    return metric === 'Vibration' ? { x: 0, y: 0, z: 0 } : { value: 0 };
  }

  if (metric === 'Temperature' || metric === 'Magnetometer') {
    // [85] -> { value: 85 }
    return { value: threshold[0] || 0 };
  } if (metric === 'Vibration') {
    // [8.0, 8.0, 8.0] -> { x: 8.0, y: 8.0, z: 8.0 }
    return {
      x: threshold[0] || 0,
      y: threshold[1] || 0,
      z: threshold[2] || 0
    };
  }
  return { value: 0 };
};

/**
 * Convert axis array from user format to device format
 */
export const convertAxisToDevice = (metric) => {
  if (metric === 'Temperature' || metric === 'Magnetometer') {
    return [0]; // NA
  } if (metric === 'Vibration') {
    return [1, 2, 3]; // X, Y, Z
  }
  return [0];
};

/**
 * Convert single condition from user-friendly format to device format
 */
export const conditionToDevice = (condition) => {
  const sensorType = (METRIC_TO_SENSOR_TYPE[condition.metric] != null) ? METRIC_TO_SENSOR_TYPE[condition.metric] : 0;
  const parameter = (METRIC_TYPE_TO_PARAMETER[condition.metricType] != null) ? METRIC_TYPE_TO_PARAMETER[condition.metricType] : 0;
  const operator = (OPERATOR_TO_ENUM[condition.operator] != null) ? OPERATOR_TO_ENUM[condition.operator] : 0;
  const threshold = convertThresholdsToDevice(condition.thresholds, condition.metric);
  const axis = condition.axis || convertAxisToDevice(condition.metric);

  return {
    sensor_type: sensorType,
    parameter,
    operator,
    threshold,
    axis
  };
};

/**
 * Convert single condition from device format to user-friendly format
 */
export const conditionToUser = (condition) => {
  const metric = SENSOR_TYPE_TO_METRIC[condition.sensor_type] || 'Temperature';
  const metricType = PARAMETER_TO_METRIC_TYPE[condition.parameter] || 'Temperature';
  const operator = ENUM_TO_OPERATOR[condition.operator] || '>';
  const thresholds = convertThresholdsToUser(condition.threshold, metric);

  return {
    metric,
    metricType,
    operator,
    thresholds,
    axis: condition.axis,
    is_coupled: condition.is_coupled || false
  };
};

/**
 * Convert entire event config from user-friendly format to device format
 */
export const toDeviceFormat = (userConfig) => {
  if (!userConfig || !userConfig.conditions) {
    return null;
  }

  // Transform coupling_config if present
  // Backend expects temperature_threshold_type and vibration_threshold_type
  // Frontend uses threshold_level as a unified value
  let couplingConfig = null;
  if (userConfig.coupling_config) {
    couplingConfig = { ...userConfig.coupling_config };
    // Map threshold_level to temperature_threshold_type and vibration_threshold_type
    if (couplingConfig.threshold_level) {
      const thresholdLevel = couplingConfig.threshold_level;
      // Only set if not already present (allow explicit override)
      if (!couplingConfig.temperature_threshold_type) {
        couplingConfig.temperature_threshold_type = thresholdLevel;
      }
      if (!couplingConfig.vibration_threshold_type) {
        couplingConfig.vibration_threshold_type = thresholdLevel;
      }
    }
  }

  return {
    coupling_config: couplingConfig,
    conditions: userConfig.conditions.map(conditionToDevice),
    condition_logic: (LOGIC_TO_ENUM[userConfig.logic] != null) ? LOGIC_TO_ENUM[userConfig.logic] : 0,
    actions: [(ACTION_TO_ENUM[userConfig.action] != null) ? ACTION_TO_ENUM[userConfig.action] : 0]
  };
};

/**
 * Convert entire event config from device format to user-friendly format
 */
export const toUserFormat = (deviceConfig) => {
  if (!deviceConfig || !deviceConfig.conditions) {
    return null;
  }

  // Transform coupling_config from backend format to UI format
  // Backend sends temperature_threshold_type and vibration_threshold_type
  // UI uses threshold_level as a unified value
  let couplingConfig = null;
  if (deviceConfig.coupling_config) {
    couplingConfig = { ...deviceConfig.coupling_config };
    // If both threshold types are the same, use threshold_level
    const tempType = couplingConfig.temperature_threshold_type;
    const vibType = couplingConfig.vibration_threshold_type;
    if (tempType && vibType && tempType === vibType) {
      couplingConfig.threshold_level = tempType;
      // Remove the separate fields to keep UI format clean
      delete couplingConfig.temperature_threshold_type;
      delete couplingConfig.vibration_threshold_type;
    } else if (tempType || vibType) {
      // If they differ, keep both but also set threshold_level to the first available
      couplingConfig.threshold_level = tempType || vibType || 'critical';
    }
  }

  return {
    conditions: deviceConfig.conditions.map(conditionToUser),
    logic: ENUM_TO_LOGIC[deviceConfig.condition_logic] || 'AND',
    action: ENUM_TO_ACTION[(deviceConfig.actions && deviceConfig.actions[0])] || 'Immediate Upload',
    coupling_config: couplingConfig
  };
};

/**
 * Convert array of user configs to device format
 */
export const toDeviceFormatArray = (userConfigs) => {
  if (!Array.isArray(userConfigs)) {
    return [];
  }
  return userConfigs.map(toDeviceFormat).filter(config => config !== null);
};

/**
 * Convert array of device configs to user format
 */
export const toUserFormatArray = (deviceConfigs) => {
  if (!Array.isArray(deviceConfigs)) {
    return [];
  }
  return deviceConfigs.map(toUserFormat).filter(config => config !== null);
};

/**
 * Map condition metricType to coupling feature name
 */
const metricTypeToFeature = (metricType) => {
  if (metricType === 'Velocity RMS' || metricType === 'Velocity Peak' || metricType === 'Velocity P2P') {
    return 'velocity';
  }
  if (metricType === 'Accel RMS' || metricType === 'Accel Peak' || metricType === 'Accel P2P') {
    return 'acceleration';
  }
  return null;
};

/**
 * Check if a condition is coupled based on coupling_config
 * For vibration, also checks if the specific feature (metricType) is coupled
 */
export const isConditionCoupled = (condition, couplingConfig) => {
  if (!couplingConfig || !couplingConfig.coupled_sensor_types) {
    return false;
  }

  // Speed has no alarm threshold to draw from. Treated as coupled it would lose
  // its required threshold and be saved without one. Mirrors the backend's
  // is_condition_couplable.
  if (condition.metricType === SPEED_METRIC_TYPE) {
    return false;
  }

  const sensorType = METRIC_TO_SENSOR_TYPE[condition.metric];

  // Check if sensor type is coupled
  if (!couplingConfig.coupled_sensor_types.includes(sensorType)) {
    return false;
  }

  // For vibration, also check if the specific feature is coupled
  if (condition.metric === 'Vibration') {
    const conditionFeature = metricTypeToFeature(condition.metricType);
    const vibrationFeatures = couplingConfig.vibration_features || [];

    // Check if this condition's feature is in the coupled features
    return conditionFeature !== null && vibrationFeatures.includes(conditionFeature);
  }

  // For temperature and other sensor types, just check sensor type
  return true;
};

/**
 * Get sensor type enum from metric name
 */
export const getSensorTypeFromMetric = metric => (METRIC_TO_SENSOR_TYPE[metric] != null) ? METRIC_TO_SENSOR_TYPE[metric] : 0;

/**
 * Get metric name from sensor type enum
 */
export const getMetricFromSensorType = sensorType => SENSOR_TYPE_TO_METRIC[sensorType] || 'Temperature';
